import React, { useRef, useState, useEffect, useCallback } from "react";
import { useLogo } from '../../hooks';

function ChatTongole() {
  // Determine if chat toggle should be hidden (xbet50 origin)
  const hideChat = window.location.hostname.toLowerCase().includes('xbet50');

  const chatRef = useRef(null);
  const draggingRef = useRef(false);
  const dragDistanceRef = useRef(0);
  const openChatHandlerRef = useRef(null);
  const mountedRef = useRef(true);

  // Use shared logo hook instead of duplicate fetch
  const { mobileLogo } = useLogo();

  const [position, setPosition] = useState({
    x: window.innerWidth - 70,
    y: window.innerHeight - 150,
  });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [show, setShow] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);

  // Initialize chat display mode on mount only (fixes circular useEffect)
  useEffect(() => {
    mountedRef.current = true;
    const liveChat = localStorage.getItem("liveChat");
    const whatsapp = localStorage.getItem("whatsapp");

    if (liveChat && liveChat !== 'null') {
      setShow('live_chat');
    } else {
      setShow('base_chat');
    }
    setWhatsappLink((whatsapp && whatsapp !== 'null') ? whatsapp : null);

    return () => { mountedRef.current = false; };
  }, []); // Empty deps - runs once on mount

  // LiveChat widget initialization
  useEffect(() => {
    if (window.location.origin === "https://www.chat.com") {
      window.__lc = window.__lc || {};
      window.__lc.license = 19116631;

      (function (n, t, c) {
        function i(n) {
          return e._h ? e._h.apply(null, n) : e._q.push(n);
        }
        var e = {
          _q: [],
          _h: null,
          _v: "2.0",
          on: function () { i(["on", c.call(arguments)]); },
          once: function () { i(["once", c.call(arguments)]); },
          off: function () { i(["off", c.call(arguments)]); },
          get: function () {
            if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
            return i(["get", c.call(arguments)]);
          },
          call: function () { i(["call", c.call(arguments)]); },
          init: function () {
            var n = t.createElement("script");
            n.async = !0;
            n.type = "text/javascript";
            n.src = "https://cdn.livechatinc.com/tracking.js";
            t.head.appendChild(n);
          },
        };
        !n.__lc.asyncInit && e.init();
        n.LiveChatWidget = n.LiveChatWidget || e;
      })(window, document, [].slice);
    }
  }, []);

  // Chat click handler (memoized to maintain stable reference)
  const handleChatClick = useCallback((e) => {
    if (e) e.preventDefault();

    let wa = whatsappLink;
    const storedWa = localStorage.getItem("whatsapp");
    if (!wa || wa === 'null') {
      wa = (storedWa && storedWa !== 'null') ? storedWa : null;
      setWhatsappLink(wa);
    }

    const remoteId = localStorage.getItem("remote_id");
    const username = localStorage.getItem("username");

    if (window.Intercom) {
      window.Intercom("show");
    } else if (wa && wa !== 'null' && wa.length > 0) {
      window.open(wa, "_blank");
    } else {
      if (window?.anw2 !== undefined) {
        window.anw2.open();
      }
      if (window.LiveChatWidget) {
        window.LiveChatWidget.once("ready", () => {
          if (remoteId && username) {
            window.LiveChatWidget.call("set_customer_data", {
              name: username,
              external_id: remoteId,
            });
          }
          window.LiveChatWidget.call("maximize");
        });
      }
      if (window.Tawk_API) {
        window.Tawk_API.maximize();
      }
    }
  }, [whatsappLink]);

  // ── Listen for custom 'open_chat' event (with proper cleanup) ──
  useEffect(() => {
    openChatHandlerRef.current = handleChatClick;
    const listener = () => {
      if (openChatHandlerRef.current) openChatHandlerRef.current();
    };
    window.addEventListener('open_chat', listener);
    return () => {
      window.removeEventListener('open_chat', listener);
    };
  }, [handleChatClick]);

  // ── Drag handlers ──────────────────────────────────────────────────
  const handleStart = useCallback((e) => {
    draggingRef.current = true;
    dragDistanceRef.current = 0;

    const event = e.type === "touchstart" ? e.touches[0] : e;
    const rect = chatRef.current?.getBoundingClientRect();
    if (!rect) return;

    setOffset({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });

    if (e.type === "mousedown") {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
    } else if (e.type === "touchstart") {
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);
    }
  }, []);

  const handleMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const event = e.type === "touchmove" ? e.touches[0] : e;
    e.preventDefault();

    const newX = event.clientX - offset.x;
    const newY = event.clientY - offset.y;

    dragDistanceRef.current = Math.sqrt(
      Math.pow(newX - position.x, 2) + Math.pow(newY - position.y, 2)
    );

    setPosition({ x: newX, y: newY });
  }, [offset, position]);

  const handleEnd = useCallback((e) => {
    if (dragDistanceRef.current > 10) {
      e?.preventDefault();
    }
    draggingRef.current = false;

    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
    document.removeEventListener("touchmove", handleMove);
    document.removeEventListener("touchend", handleEnd);
  }, [handleMove]);

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [handleMove, handleEnd]);

  if (hideChat) return null;

  // Hide toggle when data has loaded but no valid chat configured
  if (show !== null && show !== 'live_chat' && !whatsappLink) return null;

  // Static inline styles (outside render to avoid recreation)
  const chatStyle = {
    position: "fixed",
    left: `${position.x}px`,
    top: `${position.y}px`,
    cursor: "grab",
    zIndex: 1000,
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: "rgb(255 138 20)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  };

  return (
    <>
      <style>{`[id^="anw2-launcher-container-"] [id^="anw2-launcher-"]{display:none!important}`}</style>
      <a href="/#" onClick={handleChatClick} style={{ textDecoration: 'none' }}>
        <div
          ref={chatRef}
          className="chat-tongole"
          style={chatStyle}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          {mobileLogo ? (
            <img src={mobileLogo} alt="Chat Icon" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
          ) : (
            <span style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>💬</span>
          )}
        </div>
      </a>
    </>
  );
}

export default ChatTongole;
